import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from '@kollektive/design-system';
import { Globe } from 'lucide-react';
import { apiClient } from '@/api';
import { useAuth } from '@/hooks/useAuth';
import styles from './AtlassianConnectionModal.module.css';

interface AtlassianConnectionModalProps {
  open: boolean;
  onClose: () => void;
}

export function AtlassianConnectionModal({ open, onClose }: AtlassianConnectionModalProps) {
  const { session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.post<{ auth_url: string }>(
        '/api/v1/integrations/atlassian/connect',
        { redirect_uri: window.location.href },
        { token: session?.access_token }
      );
      window.location.href = result.auth_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate Atlassian connection');
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="md">
      <ModalHeader
        title="Connect Atlassian"
        description="Link your Confluence and JIRA workspace"
        onClose={onClose}
      />
      <ModalBody>
        <div className={styles.content}>
          <div className={styles.iconWrapper}>
            <Globe size={32} style={{ color: '#0052CC' }} />
          </div>
          <p className={styles.description}>
            Connect your Atlassian workspace to enable AI-powered access to Confluence pages
            and JIRA issues. You&apos;ll be redirected to Atlassian to authorize access.
          </p>
          <div className={styles.features}>
            <div className={styles.feature}>
              <span className={styles.featureLabel}>Confluence</span>
              <span className={styles.featureDetail}>Search, read, and create pages</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureLabel}>JIRA</span>
              <span className={styles.featureDetail}>Search, create, and manage issues</span>
            </div>
          </div>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnect} disabled={loading}>
            <Globe size={16} />
            {loading ? 'Connecting...' : 'Connect with Atlassian'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
